import { AICustomerInsights, useRestApiProvider } from "@jugl-web/rest-api";
import { DataLoadingWrapper } from "@jugl-web/ui-components/cross-platform/content/DataLoadingWrapper";
import { SidebarDrawer } from "@jugl-web/ui-components/web/SidebarDrawer";
import { Avatar } from "@jugl-web/ui-kit/primitives";
import { cx } from "@jugl-web/utils";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { FC, useMemo } from "react";
import { ReactComponent as AIIcon } from "./assets/ai.svg";
import { ReactComponent as StarIcon } from "./assets/star.svg";

interface HandoverInsightsContentSidebarProps {
  isOpen: boolean;
  conversationId: string | undefined;
  onClose: () => void;
}

export const HandoverInsightsSidebar: FC<
  HandoverInsightsContentSidebarProps
> = ({ isOpen, conversationId, onClose }) => (
  <SidebarDrawer
    className="rounded-l-2xl"
    isOpen={isOpen}
    title={
      <div className="flex items-center gap-3">
        <AIIcon className="shrink-0" />
        <span>Handover Insights</span>
      </div>
    }
    onClose={onClose}
  >
    {conversationId && (
      <HandoverInsightsSidebarContent conversationId={conversationId} />
    )}
  </SidebarDrawer>
);

const HandoverInsightsSidebarContent: FC<{ conversationId: string }> = ({
  conversationId,
}) => {
  const { entityId } = useEntitySelectedProvider();

  const { integrationsApi } = useRestApiProvider();

  const { data, isLoading, isError, refetch } =
    integrationsApi.useGetWebChatBotCustomerInsightsQuery(
      conversationId ? { entityId, conversationId } : skipToken
    );

  return (
    <SidebarDrawer.Content className="p-8">
      <DataLoadingWrapper
        isLoading={isLoading}
        isError={isError}
        onRetry={() => refetch()}
      >
        {data && <HandoverInsightsContent data={data} />}
      </DataLoadingWrapper>
    </SidebarDrawer.Content>
  );
};

function formatMemberSince(isoDate: string): string {
  const parsed = new Date(isoDate);

  if (Number.isNaN(parsed.getTime())) {
    return isoDate;
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    year: "numeric",
  }).format(parsed);
}

function formatUsd(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatReturnRatePercent(rate: number): number {
  return Math.round(rate * 100);
}

function isHostileSentiment(sentiment: string): boolean {
  return /hostile|negative|angry|frustrated|aggressive/i.test(sentiment);
}

type AbuseSeverity = "critical" | "warning";

function buildAbusePatternRows(
  data: AICustomerInsights
): { severity: AbuseSeverity; title: string; description: string }[] {
  const { customer, customer_360: customer360, decision } = data;

  const rows: Array<{
    severity: AbuseSeverity;
    title: string;
    description: string;
  }> = [];

  if (customer360.returns_last_90_days > 0) {
    const ratePct = formatReturnRatePercent(customer360.return_rate);

    rows.push({
      severity: customer360.return_rate >= 30 ? "critical" : "warning",
      title: `${customer360.returns_last_90_days} returns in last 90 days`,
      description: `Return rate: ${ratePct}% (avg customer: 8%)`,
    });
  }

  //   if (customer360.total_refunded > 0) {
  //     const severity: AbuseSeverity =
  //       customer360.total_refunded > customer360.ltv * 2 ? "critical" : "warning";
  //     rows.push({
  //       severity,
  //       title: `${formatUsd(customer360.total_refunded)} total refunded`,
  //       description: `Lifetime value: ${formatUsd(customer360.ltv)}`,
  //     });
  //   }

  //   if (customer360.sentiment.trim().length > 0) {
  //     const hostile = isHostileSentiment(customer360.sentiment);
  //     rows.push({
  //       severity: hostile ? "critical" : "warning",
  //       title: `Sentiment: ${customer360.sentiment}`,
  //       description: hostile
  //         ? "Escalation risk based on recent tone signals"
  //         : "As detected from recent interactions",
  //     });
  //   }

  //   if (customer.tier.trim().length > 0) {
  //     rows.push({
  //       severity: "warning",
  //       title: `Account tier: ${customer.tier}`,
  //       description: `Risk score: ${decision.risk_score}/100`,
  //     });
  //   }

  return rows;
}

const HandoverInsightsContent: FC<{ data: AICustomerInsights }> = ({
  data,
}) => {
  const abuseRows = useMemo(() => buildAbusePatternRows(data), [data]);

  const {
    customer,
    decision,
    customer_360: customer360,
    ai_recommendation: recommendation,
  } = data;

  const showRiskBadge = decision.risk_score >= 50;
  const showAvatarAlert = decision.risk_score >= 60;
  const refundedIsDanger = customer360.total_refunded > customer360.ltv;
  const sentimentIsDanger = isHostileSentiment(customer360.sentiment);

  return (
    <div className="flex flex-col gap-8">
      <header className="flex items-center gap-4">
        {/* Avatar */}
        <div className="relative shrink-0">
          <Avatar
            username={customer.name}
            size="lg"
            className="shadow-[0_0_8.12px_0_rgba(0,0,0,0.07)_inset]"
          />
          {showAvatarAlert && (
            <span className="absolute bottom-0 right-0 flex h-[18px] w-[18px] items-center justify-center rounded-full border-2 border-solid border-white bg-[#E53935] text-[11px] font-bold text-white">
              !
            </span>
          )}
        </div>
        {/* Customer name, risk badge, and email */}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-base font-medium text-black">
              {customer.name}
            </span>
            {showRiskBadge && (
              <span className="shrink-0 rounded-full bg-[#fcebeb] px-2 py-0.5 text-[11px] font-medium text-[#791f1f]">
                {`Risk: ${decision.risk_score}/100`}
              </span>
            )}
          </div>
          <span className="text-xs text-[#5f5e5a]">
            {`${customer.email} • Customer since ${formatMemberSince(
              customer.customer_since
            )}`}
          </span>
        </div>
      </header>

      {/* Why AI transferred box */}
      {decision.ai_action.trim().length > 0 && (
        <div className="flex flex-col gap-2 rounded-lg bg-[#faeeda] px-3 py-[14px]">
          <div className="flex items-center gap-1.5">
            <div className="h-1.5 w-1.5 rounded-full bg-[#ba7517]" />
            <span className="text-[11px] font-medium uppercase text-[#854f0b]">
              Why AI transferred
            </span>
          </div>
          <span className="font-base text-sm leading-[21px] text-[#412402]">
            {decision.intent}
          </span>
        </div>
      )}

      {/* Abuse patterns detected */}
      {abuseRows.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-[11px] font-medium uppercase text-[#5f5e5a]">
            🚩 Abuse patterns detected
          </span>
          <div className="flex flex-col rounded-lg border border-solid border-[#f7c1c1]">
            {abuseRows.map((row, index) => (
              <div
                key={+index}
                className={cx(
                  "flex items-start gap-2.5 px-3 py-[14px]",
                  index !== 0 &&
                    "border-t border-l-0 border-r-0 border-b-0 border-solid border-[#E5E3DC]"
                )}
              >
                <AbusePatternIcon severity={row.severity} />
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="text-sm font-medium text-[#2C2C2A]">
                    {row.title}
                  </span>
                  <span className="font-base text-xs text-[#5c5e5a]">
                    {row.description}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Key metrics */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <MetricTile label="LTV" value={formatUsd(customer360.ltv)} />
        <MetricTile
          label="Refunded"
          value={formatUsd(customer360.total_refunded)}
          valueClassName={refundedIsDanger ? "text-[#A32D2D]" : undefined}
        />
        <MetricTile
          label="Sentiment"
          value={customer360.sentiment}
          valueClassName={sentimentIsDanger ? "text-[#A32D2D]" : undefined}
        />
        <MetricTile label="Intent" value={customer360.intent} />
      </div>

      {/* AI recommendation */}
      <div className="flex flex-col gap-2 rounded-lg bg-[#EEEDFE] px-3 py-[14px]">
        <div className="flex items-center gap-1.5">
          <StarIcon className="shrink-0" />
          <span className="text-[11px] font-medium uppercase text-[#3C3489]">
            AI recommendation
          </span>
        </div>
        <span className="text-sm leading-[150%] text-[#311B92]">
          <span className="font-medium">{recommendation.action}. </span>
          <span className="font-base">{recommendation.steps.join(". ")}</span>
        </span>
      </div>
    </div>
  );
};

const AbusePatternIcon: FC<{ severity: AbuseSeverity }> = ({ severity }) => {
  const isCritical = severity === "critical";
  return (
    <div
      className={cx(
        "flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-sm font-bold",
        isCritical
          ? "bg-[#FCEBEB] text-[#791F1F]"
          : "bg-[#FAEEDA] text-[#854F0B]"
      )}
      aria-hidden
    >
      !
    </div>
  );
};

const MetricTile: FC<{
  label: string;
  value: string;
  valueClassName?: string;
}> = ({ label, value, valueClassName }) => (
  <div className="flex flex-col gap-0.5 rounded-lg bg-[#F5F4EE] p-2.5">
    <span className="font-base text-xs text-[#5F5E5A]">{label}</span>
    <span
      className={cx(
        "truncate text-[13px] font-medium text-[#2c2c2a]",
        valueClassName
      )}
    >
      {value}
    </span>
  </div>
);
